import queryString from 'query-string'
import { computed, ComputedRef } from 'vue'

import {
  DoctorAvailabilityType,
  UserType,
  InviteType,
  QueryParamsType,
  CalendarSettingsType,
  CalendarInputSettings,
  UserInputType,
} from '@/types'
import { useApi } from '@/utils'

export function getInvites() {
  const { apiFetch } = useApi()

  return apiFetch<InviteType[]>({
    url: '/users/invites',
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function getCoordinators(params: ComputedRef<QueryParamsType>) {
  const { apiFetch } = useApi()
  const url = computed(() => {
    const qs = queryString.stringify(params.value, { arrayFormat: 'bracket' })
    return `/users/coordinators${qs ? `?${qs}` : ''}`
  })

  return apiFetch<UserType[]>({
    url,
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function getProviders(params: ComputedRef<QueryParamsType>) {
  const { apiFetch } = useApi()
  const url = computed(() => {
    const qs = queryString.stringify(params.value, { arrayFormat: 'bracket' })
    return `/users/providers${qs ? `?${qs}` : ''}`
  })

  return apiFetch<UserType[]>({
    url,
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function getUsers(params: ComputedRef<QueryParamsType>) {
  const { apiFetch } = useApi()

  const url = computed(() => {
    const qs = queryString.stringify(params.value)
    return `/users${qs ? `?${qs}` : ''}`
  })

  return apiFetch<UserType[]>({
    url,
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function getRolesByClinic(clinicId: ComputedRef<number>) {
  const { apiFetch } = useApi()
  const url = computed(() => `/websites/${clinicId.value}/users/invites`)

  return apiFetch<InviteType[]>({
    url,
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function getUserProfile() {
  const { apiFetch } = useApi()

  return apiFetch<UserType>({
    url: '/users/profile',
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}

export function useUserApi() {
  const { apiFetch } = useApi()

  const getDoctorAvailability = (id: string, params: QueryParamsType) => {
    const qs = queryString.stringify(params)

    return apiFetch<DoctorAvailabilityType>({
      url: `/users/${id}/calendars/availability${qs ? `?${qs}` : ''}`,
      method: 'GET',
    })
  }

  const sendInvite = (clinicId: number, formData: FormData) => {
    return apiFetch<InviteType>({
      url: `/websites/${clinicId}/users/invites`,
      method: 'POST',
      fetchOptions: { body: formData },
    })
  }

  const saveMyProfile = (values: UserInputType) => {
    return apiFetch<UserType>({
      url: '/users/profile',
      method: 'POST',
      fetchOptions: {
        body: JSON.stringify(values),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    })
  }

  const saveUserSettings = (userId: number, values: CalendarInputSettings) => {
    return apiFetch<CalendarSettingsType>({
      url: `/users/${userId}/calendars/settings`,
      method: 'POST',
      fetchOptions: {
        body: JSON.stringify(values),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    })
  }

  const saveUserProfile = (userId: number, values: UserInputType) => {
    return apiFetch<CalendarSettingsType>({
      url: `/users/${userId}/profile`,
      method: 'POST',
      fetchOptions: {
        body: JSON.stringify(values),
        headers: {
          'Content-Type': 'application/json',
        },
      },
    })
  }

  return { getDoctorAvailability, sendInvite, saveMyProfile, saveUserSettings, saveUserProfile }
}

export function getUserSettings(userId: ComputedRef<number>) {
  const { apiFetch } = useApi()
  const url = computed(() => `/users/${userId.value}/calendars/settings`)

  return apiFetch<CalendarSettingsType>({
    url,
    method: 'GET',
    useFetchOptions: { immediate: false },
  })
}
